import axios from 'axios'

import client from '@/shared/apis/client'
import { API_HOST } from '@/shared/constants/const'
import { convertLocalhostTo127 } from '@/shared/hooks/useLocalhostRedirect'

import {
  type CounselCasesResponse,
  type CounselFinishResponse,
  type CounselTurnResponse,
} from '../types/counsel'

const BASE_URL = '/api/counsel'

// 読み上げの URL。流しを fetch で読むので axios を通さず、base の継ぎ方を axios に合わせる。
// 同一オリジンの build は API_HOST = '/' で、そのまま前に足すと `//api/...` (= api という別のホスト) になる
export const counselSpeechUrl = (
  token: string,
  index: number,
  sessionId: string | null,
  host: string = API_HOST ?? ''
): string => {
  const query = new URLSearchParams({ n: String(index) })
  if (sessionId) query.set('session_id', sessionId)
  const base = convertLocalhostTo127(host).replace(/\/+$/, '')
  return `${base}${BASE_URL}/${token}/speech/?${query}`
}

// 1 発言の字数上限。**back の `serializers.TEXT_MAX_CHARS` と対**
export const COUNSEL_TEXT_MAX_CHARS = 1000
// 感想の字数上限。**back の `serializers.FEEDBACK_MAX_CHARS` と対**
export const COUNSEL_FEEDBACK_MAX_CHARS = 2000

export const getCounselCases = async (
  token: string
): Promise<CounselCasesResponse> => {
  const response = await client.get<CounselCasesResponse>(
    `${BASE_URL}/${token}/cases/`
  )
  return response.data
}

// 最初の発言は case_key で会話を作り、以降は session_id で続ける。
// 書類の写真があるときは multipart で送る (text は空でよい)
export const postCounselTurn = async (
  token: string,
  body: { session_id?: string; case_key?: string; text: string },
  photo?: Blob
): Promise<CounselTurnResponse> => {
  const url = `${BASE_URL}/${token}/turn/`
  if (!photo) {
    const response = await client.post<CounselTurnResponse>(url, body)
    return response.data
  }
  const form = new FormData()
  Object.entries(body).forEach(([key, value]) => {
    if (value) form.append(key, value)
  })
  form.append('photo', photo, 'photo.jpg')
  const response = await client.post<CounselTurnResponse>(url, form)
  return response.data
}

// 1 回目で要点を作る。感想を付けて 2 回目を呼んでも要点は作り直さない
export const postCounselFinish = async (
  token: string,
  body: { session_id: string; feedback?: string }
): Promise<CounselFinishResponse> => {
  const response = await client.post<CounselFinishResponse>(
    `${BASE_URL}/${token}/finish/`,
    body
  )
  return response.data
}

// mic で話した音声を、欄に入れる文に起こす。聞き取れなければ 422 と 1 行が返る
export const postCounselTranscribe = async (
  token: string,
  audio: Blob
): Promise<string> => {
  const form = new FormData()
  form.append(
    'audio',
    audio,
    audio.type.includes('mp4') ? 'voice.m4a' : 'voice.webm'
  )
  const response = await client.post<{ text: string }>(
    `${BASE_URL}/${token}/transcribe/`,
    form
  )
  return response.data.text
}

// 失敗の 1 行。back が理由を返す失敗 (生成できなかった・終わった会話) はその文を出す
export const counselErrorMessage = (error: unknown): string => {
  const detail = axios.isAxiosError(error)
    ? error.response?.data?.detail
    : undefined
  return typeof detail === 'string'
    ? detail
    : '送れませんでした。もう一度送ってください。'
}
